import React, { useState } from 'react'
import { Calendar, Clock, User, Tag, ArrowRight, TrendingUp, MessageCircle, Heart, Share2, Bookmark, Search, Filter } from 'lucide-react'

interface Web3BlogProps {
  data: {
    blogName: string
    description: string
    author: {
      name: string
      avatar: string
      bio: string
      socialLinks: {
        twitter: string
        github: string
        linkedin: string
      }
    }
    posts: Array<{
      id: string
      title: string
      excerpt: string
      content: string
      author: string
      publishedAt: string
      readTime: string
      category: string
      tags: string[]
      views: number
      likes: number
      comments: number
      featured: boolean
      cryptoIntegration?: {
        token: string
        price: string
        change: number
      }
    }>
    categories: Array<{
      id: string
      name: string
      count: number
      color: string
    }>
    newsletter: {
      title: string
      description: string
      subscribers: number
    }
    featuredPosts: Array<{
      id: string
      title: string
      excerpt: string
      image: string
      category: string
    }>
  }
}

export default function Web3Blog({ data }: Web3BlogProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set())
  const [bookmarkedPosts, setBookmarkedPosts] = useState<Set<string>>(new Set())

  const toggleLike = (postId: string) => {
    const newLikedPosts = new Set(likedPosts)
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId)
    } else {
      newLikedPosts.add(postId)
    }
    setLikedPosts(newLikedPosts)
  }

  const toggleBookmark = (postId: string) => {
    const newBookmarkedPosts = new Set(bookmarkedPosts)
    if (newBookmarkedPosts.has(postId)) {
      newBookmarkedPosts.delete(postId)
    } else {
      newBookmarkedPosts.add(postId)
    }
    setBookmarkedPosts(newBookmarkedPosts)
  }

  const filteredPosts = data.posts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesCategory && matchesSearch
  })

  const featuredPosts = data.posts.filter(post => post.featured)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-blue-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.blogName}
                </h1>
                <p className="text-blue-200 text-sm">{data.description}</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <a href="#home" className="text-white/80 hover:text-white transition-colors">Home</a>
              <a href="#posts" className="text-white/80 hover:text-white transition-colors">Posts</a>
              <a href="#about" className="text-white/80 hover:text-white transition-colors">About</a>
              <a href="#newsletter" className="text-white/80 hover:text-white transition-colors">Newsletter</a>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 
            className="text-4xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Web3 Insights & Analysis
          </h2>
          <p className="text-xl md:text-2xl text-blue-200 mb-12 max-w-3xl mx-auto">
            Stay ahead of the curve with the latest blockchain developments, DeFi strategies, and crypto market analysis.
          </p>
          
          {/* Newsletter Signup */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">{data.newsletter.title}</h3>
            <p className="text-blue-200 mb-6">{data.newsletter.description}</p>
            <div className="flex gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Subscribe
              </button>
            </div>
            <p className="text-blue-300 text-sm mt-3">{data.newsletter.subscribers.toLocaleString()} subscribers</p>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="py-16 px-6 bg-black/20">
          <div className="max-w-6xl mx-auto">
            <h3 
              className="text-3xl font-bold mb-8"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Featured Posts
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-blue-400/50 transition-all duration-300 group"
                >
                  <div className="aspect-video bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="w-8 h-8 text-blue-400" />
                      </div>
                      <p className="text-blue-300 text-sm">{post.category}</p>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs font-semibold">
                        Featured
                      </span>
                      <span className="text-purple-400 text-sm">{post.category}</span>
                    </div>
                    
                    <h4 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-300 transition-colors">
                      {post.title}
                    </h4>
                    
                    <p className="text-blue-200 mb-4 line-clamp-3">{post.excerpt}</p>
                    
                    <div className="flex items-center justify-between text-sm text-blue-300">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {post.publishedAt}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                    
                    {post.cryptoIntegration && (
                      <div className="mt-4 p-3 bg-green-500/10 border border-green-400/20 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-green-400 font-semibold">{post.cryptoIntegration.token}</span>
                          <div className="text-right">
                            <div className="text-white font-bold">{post.cryptoIntegration.price}</div>
                            <div className={`text-sm ${post.cryptoIntegration.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {post.cryptoIntegration.change >= 0 ? '+' : ''}{post.cryptoIntegration.change}%
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="lg:w-2/3">
              {/* Search and Filter */}
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  />
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-blue-400"
                >
                  <option value="all">All Categories</option>
                  {data.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Posts Grid */}
              <div className="space-y-8">
                {filteredPosts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:border-blue-400/50 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span 
                          className="px-3 py-1 rounded-full text-sm font-semibold"
                          style={{ 
                            backgroundColor: data.categories.find(c => c.id === post.category)?.color + '20',
                            color: data.categories.find(c => c.id === post.category)?.color
                          }}
                        >
                          {post.category}
                        </span>
                        {post.featured && (
                          <span className="bg-gold-500/20 text-gold-400 px-2 py-1 rounded text-xs font-semibold">
                            Featured
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleBookmark(post.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            bookmarkedPosts.has(post.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          <Bookmark className="w-4 h-4" />
                        </button>
                        <button className="p-2 bg-white/10 text-white/70 hover:bg-white/20 rounded-lg transition-colors">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white mb-4 hover:text-blue-300 transition-colors cursor-pointer">
                      {post.title}
                    </h2>
                    
                    <p className="text-blue-200 mb-6 text-lg leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-4 text-blue-300">
                        <span className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {post.author}
                        </span>
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {post.publishedAt}
                        </span>
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-blue-300">
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          {post.views.toLocaleString()}
                        </span>
                        <button
                          onClick={() => toggleLike(post.id)}
                          className={`flex items-center gap-1 transition-colors ${
                            likedPosts.has(post.id) ? 'text-red-400' : 'text-blue-300 hover:text-red-400'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                          {post.likes + (likedPosts.has(post.id) ? 1 : 0)}
                        </button>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {post.comments}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="bg-white/10 text-blue-300 px-2 py-1 rounded text-sm hover:bg-white/20 transition-colors cursor-pointer"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      
                      <button className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/3">
              <div className="sticky top-24 space-y-8">
                {/* Categories */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">Categories</h3>
                  <div className="space-y-3">
                    {data.categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                          selectedCategory === category.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/5 text-white/80 hover:bg-white/10'
                        }`}
                      >
                        <span>{category.name}</span>
                        <span className="text-sm">{category.count}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Author Info */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">About the Author</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{data.author.name}</h4>
                      <p className="text-blue-300 text-sm">Web3 Content Creator</p>
                    </div>
                  </div>
                  <p className="text-blue-200 text-sm mb-4">{data.author.bio}</p>
                  <div className="flex gap-3">
                    {Object.entries(data.author.socialLinks).map(([platform, url]) => (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-colors"
                      >
                        <span className="text-xs text-blue-300">{platform}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">GitHub</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">LinkedIn</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">RSS</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.blogName}. Exploring the future of Web3.
          </p>
        </div>
      </footer>
    </div>
  )
}
